import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export default function GuideArticle() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">Home</Link>
                </li>
                <li className="text-foreground-400">/</li>
                <li className="text-foreground-600">Basetao Spreadsheet Complete Guide</li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                Ultimate Guide
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
                The Complete Basetao Spreadsheet Guide: Master Order Tracking, Cost Management &amp; Shipping Optimization
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                A comprehensive, step-by-step resource covering everything from building your first Basetao spreadsheet to advanced formulas, shipping cost calculators, and professional workflow optimization strategies.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> 12 min read
                </span>
              </div>
            </header>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline:
                    'The Complete Basetao Spreadsheet Guide: Master Order Tracking, Cost Management & Shipping Optimization',
                  description:
                    'A comprehensive, step-by-step resource covering everything from building your first Basetao spreadsheet to advanced formulas, shipping cost calculators, and professional workflow optimization strategies.',
                  url: `${import.meta.env.VITE_SITE_URL}/basetao-spreadsheet-guide`,
                  datePublished: '2026-08-18',
                  dateModified: '2026-08-18',
                  author: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
                  publisher: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
                  mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': `${import.meta.env.VITE_SITE_URL}/basetao-spreadsheet-guide`,
                  },
                }),
              }}
            />

            <section className="prose-custom">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Why Every Basetao Shopper Needs a Spreadsheet
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Shopping through a Chinese agent like <strong>Basetao</strong> unlocks incredible access to products on Taobao, Tmall, and 1688 at domestic Chinese prices. However, without a proper organization system, the experience quickly becomes chaotic. You are juggling product URLs from multiple sellers, varying item prices, domestic shipping fees, warehouse arrival dates, inspection photo links, and international shipping estimates — all before your parcel even leaves China.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                A <strong>Basetao spreadsheet</strong> solves this problem entirely. Think of it as your personal command center: every product you are interested in gets logged with its essential data points, every order status gets tracked visually, and every cost gets calculated automatically. What used to be scattered across browser tabs, email receipts, and mental notes becomes a single, clean, searchable document that gives you complete visibility and control.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                This guide draws on years of community experience and real-world testing to show you exactly how to build, customize, and optimize your Basetao spreadsheet — whether you are a first-time buyer or a seasoned reseller managing dozens of orders per month.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Step 1: Choose Your Spreadsheet Platform
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The vast majority of Basetao users prefer <strong>Google Sheets</strong> for several compelling reasons: it is completely free, cloud-based (accessible from phone, tablet, and desktop), supports real-time collaboration, and offers a robust formula engine that rivals desktop Excel. Google Sheets also integrates seamlessly with Google Forms if you want to build a submission form for adding new items on the go.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                <strong>Microsoft Excel</strong> remains a strong alternative for power users who need advanced features like Power Query, complex pivot tables, and VBA macros. If you already use Excel for business or accounting, sticking with it for your Basetao tracking makes sense — you can link your shopping data directly into your existing financial workflows.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                For this guide, we will focus on Google Sheets since it is the most accessible and widely used option, but the principles and column structures apply equally to Excel.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Step 2: Essential Columns for Your Basetao Spreadsheet
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The foundation of any good Basetao spreadsheet is a well-thought-out column structure. Here are the essential columns every tracker should include, organized by category:
              </p>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border border-background-200/60 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-accent-100">
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Category</th>
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Column Name</th>
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/40">
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Product Info</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Item Name</td>
                      <td className="py-2.5 px-4 text-foreground-600">Descriptive name you will recognize</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Product Info</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Product URL</td>
                      <td className="py-2.5 px-4 text-foreground-600">Direct link to the item page</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Product Info</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Size / Color / Variant</td>
                      <td className="py-2.5 px-4 text-foreground-600">Selected options to avoid mix-ups</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Pricing</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Item Price (CNY)</td>
                      <td className="py-2.5 px-4 text-foreground-600">Original price in Chinese Yuan</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Pricing</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Domestic Shipping</td>
                      <td className="py-2.5 px-4 text-foreground-600">Shipping fee from seller to Basetao warehouse</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Pricing</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Converted Price (USD)</td>
                      <td className="py-2.5 px-4 text-foreground-600">Auto-calculated using current exchange rate</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Status</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Order Status</td>
                      <td className="py-2.5 px-4 text-foreground-600">Purchased / Shipped to Warehouse / Arrived / Stored</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Status</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">QC Photos</td>
                      <td className="py-2.5 px-4 text-foreground-600">Link to inspection photos or checkmark</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Shipping</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Estimated Weight (g)</td>
                      <td className="py-2.5 px-4 text-foreground-600">Item weight for shipping calculation</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Shipping</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Shipping Method</td>
                      <td className="py-2.5 px-4 text-foreground-600">Selected carrier (DHL, EMS, SAL, etc.)</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Shipping</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">International Shipping Cost</td>
                      <td className="py-2.5 px-4 text-foreground-600">Final shipping fee for this item</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Notes</td>
                      <td className="py-2.5 px-4 font-medium text-foreground-900">Remarks</td>
                      <td className="py-2.5 px-4 text-foreground-600">Any additional notes or seller communication</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Step 3: Build Automated Formulas for Cost Tracking
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                A truly powerful Basetao spreadsheet uses formulas to eliminate manual math. Here are the key formulas every user should implement:
              </p>

              <div className="bg-background-100 rounded-lg border border-background-200/60 p-5 my-6">
                <h4 className="font-semibold text-foreground-900 mb-3">Essential Formulas</h4>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-foreground-500 mb-1">Currency Conversion (CNY to USD):</p>
                    <code className="block bg-background-50 px-3 py-2 rounded text-foreground-800 font-mono text-xs">
                      =ROUND(E2 / 7.25, 2)
                    </code>
                    <p className="text-foreground-500 mt-1 text-xs">Where E2 is the CNY price and 7.25 is the approximate exchange rate (update as needed)</p>
                  </div>
                  <div>
                    <p className="text-foreground-500 mb-1">Total Item Cost (Item + Domestic Shipping):</p>
                    <code className="block bg-background-50 px-3 py-2 rounded text-foreground-800 font-mono text-xs">
                      =SUM(E2, F2)
                    </code>
                    <p className="text-foreground-500 mt-1 text-xs">Adds the item price and domestic shipping fee together</p>
                  </div>
                  <div>
                    <p className="text-foreground-500 mb-1">Estimated Shipping Cost (Weight × Rate):</p>
                    <code className="block bg-background-50 px-3 py-2 rounded text-foreground-800 font-mono text-xs">
                      =ROUND((J2 / 1000) * 85, 2)
                    </code>
                    <p className="text-foreground-500 mt-1 text-xs">Where J2 is weight in grams, divided by 1000 to get kg, multiplied by estimated rate per kg</p>
                  </div>
                  <div>
                    <p className="text-foreground-500 mb-1">Grand Total Per Item:</p>
                    <code className="block bg-background-50 px-3 py-2 rounded text-foreground-800 font-mono text-xs">
                      =SUM(G2, K2)
                    </code>
                    <p className="text-foreground-500 mt-1 text-xs">Converted USD price plus international shipping cost</p>
                  </div>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Step 4: Add Conditional Formatting for Visual Status Tracking
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Color coding transforms your spreadsheet from a wall of text into an instant visual dashboard. In Google Sheets, use <strong>Conditional Formatting</strong> (Format → Conditional formatting) to apply automatic background colors based on cell values.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                For the Order Status column, create rules like:
              </p>
              <ul className="list-disc pl-5 space-y-2 mb-4 text-foreground-700">
                <li><strong>Not Purchased</strong> → Light red background — items still waiting for your action</li>
                <li><strong>Purchased</strong> → Light yellow background — order has been placed, awaiting seller shipment</li>
                <li><strong>Shipped to Warehouse</strong> → Light blue background — en route to Basetao facility</li>
                <li><strong>Arrived at Warehouse</strong> → Light green background — ready for consolidation and international shipping</li>
                <li><strong>Shipped Internationally</strong> → Medium green background — on its way to you!</li>
                <li><strong>Delivered</strong> → Dark green with white text — journey complete</li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Step 5: Advanced Techniques for Power Users
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Once you have the basics working, these advanced techniques can take your Basetao spreadsheet to a professional level:
              </p>

              <div className="space-y-6 my-6">
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-5">
                  <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-pie-chart-line text-accent-600"></i> Dashboard Summary Sheet
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    Create a separate sheet that aggregates key metrics: total spent, average item cost, items per shipment, estimated vs. actual shipping costs, and delivery time averages. Use <code className="bg-background-50 px-1.5 py-0.5 rounded text-xs font-mono">=SUMIF()</code>, <code className="bg-background-50 px-1.5 py-0.5 rounded text-xs font-mono">=AVERAGEIF()</code>, and <code className="bg-background-50 px-1.5 py-0.5 rounded text-xs font-mono">=COUNTIF()</code> functions to populate it automatically.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/60 p-5">
                  <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-ship-line text-accent-600"></i> Multi-Carrier Shipping Comparison
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    Add columns for estimated costs across different carriers (DHL, EMS, SAL, Sea Freight) and use formulas to highlight the cheapest option for each weight tier. This alone can save you hundreds of dollars per haul by identifying when a slightly slower method cuts costs dramatically.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/60 p-5">
                  <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-history-line text-accent-600"></i> Historical Order Archive
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    Keep completed orders in a separate archive sheet rather than deleting them. Over time, this builds a valuable dataset showing which sellers are reliable, which items have the best cost-to-weight ratio, and which shipping methods delivered fastest to your country. Future you will thank present you for this diligence.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/60 p-5">
                  <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-accent-600"></i> Reseller Profit Calculator
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    If you resell items purchased through Basetao, add columns for your selling price, marketplace fees, and packaging costs. A simple <code className="bg-background-50 px-1.5 py-0.5 rounded text-xs font-mono">=SellingPrice - (ItemCost + Shipping + Fees + Packaging)</code> formula shows your exact profit margin per item, helping you price competitively while protecting your margins.
                  </p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Common Mistakes to Avoid
              </h2>
              <div className="space-y-3 mb-6">
                {[
                  { title: 'Forgetting to update exchange rates', desc: 'CNY to USD rates fluctuate. If your spreadsheet uses a hard-coded rate from six months ago, all your cost estimates are wrong. Set a calendar reminder to update your conversion rate monthly, or better yet, use the GOOGLEFINANCE function in Google Sheets to pull live rates.' },
                  { title: 'Ignoring dimensional weight', desc: 'Many carriers charge based on dimensional (volumetric) weight, not just actual weight. A lightweight but bulky item can cost far more to ship than a compact heavy item. Track package dimensions alongside weight for accurate estimates.' },
                  { title: 'Not backing up your spreadsheet', desc: 'Google Sheets automatically saves, but accidental deletion or formula corruption happens. Make a copy of your master spreadsheet at least once a month and store it in a separate folder. Peace of mind is worth the 10 seconds it takes.' },
                  { title: 'Overcomplicating too early', desc: 'Start simple. Five columns are better than twenty-five if you actually use them consistently. Add complexity only when you feel genuine friction from missing data. A spreadsheet you maintain is infinitely more valuable than a perfect one you abandon.' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg border border-background-200/60 p-4">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1 flex items-center gap-2">
                      <i className="ri-error-warning-line text-secondary-600"></i> {item.title}
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Basetao Spreadsheet vs. Other Shopping Agents
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                While this guide focuses on Basetao, the spreadsheet methodology works equally well with any Chinese shopping agent. Here is how the core spreadsheet needs compare across popular platforms:
              </p>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border border-background-200/60 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-accent-100">
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Feature</th>
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Basetao</th>
                      <th className="text-left py-3 px-4 text-accent-800 font-semibold">Other Agents</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/40">
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Warehouse Storage</td>
                      <td className="py-2.5 px-4 text-foreground-900">Up to 180 days free</td>
                      <td className="py-2.5 px-4 text-foreground-600">Varies (typically 90-180 days)</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">QC Photos</td>
                      <td className="py-2.5 px-4 text-foreground-900">Free, automated</td>
                      <td className="py-2.5 px-4 text-foreground-600">Usually free, some charge for extras</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Order Dashboard</td>
                      <td className="py-2.5 px-4 text-foreground-900">Functional but basic</td>
                      <td className="py-2.5 px-4 text-foreground-600">Varies widely by platform</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="py-2.5 px-4 text-foreground-700">Spreadsheet Compatibility</td>
                      <td className="py-2.5 px-4 text-foreground-900">Excellent — CSV exports available</td>
                      <td className="py-2.5 px-4 text-foreground-600">Most offer export functionality</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4 text-foreground-700">Service Fees</td>
                      <td className="py-2.5 px-4 text-foreground-900">Standard percentage-based</td>
                      <td className="py-2.5 px-4 text-foreground-600">Comparable across the industry</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                Final Thoughts: Make the Spreadsheet Work for You
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The most important principle behind a successful Basetao spreadsheet is simple: <strong>it must fit your actual shopping behavior</strong>. Do not copy someone else's template verbatim if their workflow differs from yours. A reseller who ships 20kg hauls monthly needs different columns than a casual buyer who orders two items per quarter. Start with the essentials, use the spreadsheet for a few weeks, and then refine ruthlessly — remove columns you never fill, add ones that would have saved you time, and adjust formulas to match the real costs you encounter.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                This guide provides the foundation. Your experience and iteration will turn it into a personalized tool that saves you time, money, and frustration on every single Basetao order.
              </p>

              <div className="mt-10 p-6 bg-accent-50 rounded-lg border border-accent-200 text-center">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Ready to Get Started?</h3>
                <p className="text-sm text-foreground-600 mb-4">
                  Download a free Basetao spreadsheet template and start organizing your orders today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-md hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Get Free Template
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-3 border border-accent-300 text-accent-700 font-medium text-sm rounded-md hover:bg-accent-100 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                  </Link>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h4>
                <div className="flex flex-wrap gap-2">
                  <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 bg-accent-50 px-3 py-1.5 rounded-md transition-colors cursor-pointer">Basetao Spreadsheet Home</Link>
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-foreground-900 bg-background-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer">Official Store</a>
                </div>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}